import { useState, FormEvent } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useScrollReveal } from '../../hooks/useScrollReveal';

const FORM_URL = 'https://readdy.ai/api/form/d735e0mloep5vvd17jdg';

type FormStatus = 'idle' | 'sending' | 'success' | 'error';

function ContactInfo() {
  const items = [
    {
      icon: 'ri-map-pin-line',
      label: 'Adresse',
      lines: ['91 Rue Nationale', '59800 Lille, France'],
    },
    {
      icon: 'ri-phone-line',
      label: 'Téléphone',
      lines: ['+33 6 99 06 88 40'],
      link: 'tel:+33699068840',
    },
    {
      icon: 'ri-mail-line',
      label: 'Email',
      lines: ['joannawab1ch@outlook.com'],
      link: 'mailto:joannawab1ch@outlook.com',
    },
  ];

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-px bg-gray-300" />
          <span
            className="text-xs tracking-widest uppercase text-gray-400"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Coordonnées
          </span>
        </div>
        <h2
          className="text-gray-900 mb-3"
          style={{
            fontFamily: 'Cormorant Garamond, serif',
            fontWeight: 300,
            fontSize: 'clamp(28px, 3.5vw, 48px)',
            lineHeight: '1.1',
          }}
        >
          Contactez-<em style={{ fontStyle: 'italic' }}>nous</em>
        </h2>
        <p
          className="text-sm text-gray-500 leading-relaxed max-w-sm"
          style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}
        >
          Pour toute demande de projet ou d&apos;information, n&apos;hésitez pas à nous contacter. Nous répondons rapidement.
        </p>
      </div>

      <div className="space-y-6">
        {items.map((item) => (
          <div key={item.label} className="flex items-start gap-4">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#F5F5F3] flex-shrink-0 mt-0.5">
              <i className={`${item.icon} text-gray-500 text-base`} />
            </div>
            <div>
              <p
                className="text-xs text-gray-400 tracking-widest uppercase mb-1"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {item.label}
              </p>
              {item.lines.map((line) =>
                item.link ? (
                  <a
                    key={line}
                    href={item.link}
                    className="block text-sm text-gray-700 hover:text-gray-900 transition-colors cursor-pointer"
                    style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}
                  >
                    {line}
                  </a>
                ) : (
                  <p
                    key={line}
                    className="text-sm text-gray-700"
                    style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}
                  >
                    {line}
                  </p>
                )
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Company info */}
      <div className="pt-6 border-t border-gray-100">
        <p
          className="text-xs text-gray-400 tracking-widest uppercase mb-3"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          Identification
        </p>
        <div
          className="space-y-1 text-sm text-gray-500"
          style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}
        >
          <p>WABICH – Joanna Maria Wabich</p>
          <p>SIRET : 102 006 269 00017</p>
          <p>NAF : 74.10Z – Activités de design</p>
        </div>
      </div>

      {/* Google Map embed */}
      <div className="rounded-xl overflow-hidden border border-gray-100" style={{ height: '220px' }}>
        <iframe
          title="WABICH – 91 Rue Nationale, Lille"
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2530.7!2d3.0597!3d50.6316!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c2d58e92b3b1c9%3A0x0!2s91+Rue+Nationale%2C+59800+Lille!5e0!3m2!1sfr!2sfr!4v1699000000000!5m2!1sfr!2sfr"
          width="100%"
          height="220"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>
    </div>
  );
}

function ContactForm() {
  const [status, setStatus] = useState<FormStatus>('idle');
  const [charCount, setCharCount] = useState(0);
  const MAX_CHARS = 500;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value || '';
    if (message.length > MAX_CHARS) return;

    setStatus('sending');

    try {
      const formData = new URLSearchParams();
      const elements = form.elements;
      for (let i = 0; i < elements.length; i++) {
        const el = elements[i] as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
        if (el.name) {
          formData.append(el.name, el.value);
        }
      }

      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      if (response.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="bg-[#F8F8F6] rounded-2xl p-8 md:p-10">
      <h3
        className="mb-8 text-gray-900"
        style={{
          fontFamily: 'Cormorant Garamond, serif',
          fontWeight: 300,
          fontSize: '28px',
        }}
      >
        Votre <em style={{ fontStyle: 'italic' }}>demande</em>
      </h3>

      {status === 'success' ? (
        <div className="text-center py-12">
          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 rounded-full bg-gray-100">
            <i className="ri-check-line text-gray-700 text-2xl" />
          </div>
          <p
            className="text-gray-800 font-medium mb-2"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Message envoyé !
          </p>
          <p
            className="text-sm text-gray-500"
            style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}
          >
            Merci pour votre message. Nous vous répondrons dans les meilleurs délais.
          </p>
          <button
            onClick={() => setStatus('idle')}
            className="mt-6 text-sm text-gray-500 underline underline-offset-2 cursor-pointer hover:text-gray-900 transition-colors whitespace-nowrap"
            style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}
          >
            Envoyer un autre message
          </button>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit}
          data-readdy-form
          id="contact-wabich"
          className="space-y-5"
        >
          {/* Name */}
          <div>
            <label
              htmlFor="nom"
              className="block text-xs text-gray-500 tracking-widest uppercase mb-2"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Nom *
            </label>
            <input
              id="nom"
              name="nom"
              type="text"
              required
              placeholder="Votre nom complet"
              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:border-gray-500 transition-colors"
              style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}
            />
          </div>

          {/* Email */}
          <div>
            <label
              htmlFor="email"
              className="block text-xs text-gray-500 tracking-widest uppercase mb-2"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Email *
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              placeholder="votre@email.com"
              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:border-gray-500 transition-colors"
              style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}
            />
          </div>

          {/* Phone */}
          <div>
            <label
              htmlFor="telephone"
              className="block text-xs text-gray-500 tracking-widest uppercase mb-2"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Téléphone
            </label>
            <input
              id="telephone"
              name="telephone"
              type="tel"
              placeholder="+33 6 XX XX XX XX"
              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:border-gray-500 transition-colors"
              style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}
            />
          </div>

          {/* Message */}
          <div>
            <label
              htmlFor="message"
              className="block text-xs text-gray-500 tracking-widest uppercase mb-2"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Description du projet *
            </label>
            <textarea
              id="message"
              name="message"
              required
              rows={5}
              maxLength={MAX_CHARS}
              placeholder="Décrivez votre projet ou votre besoin en design..."
              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:border-gray-500 transition-colors resize-none"
              style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}
              onChange={(e) => setCharCount(e.target.value.length)}
            />
            <p
              className={`text-right text-xs mt-1 ${
                charCount > MAX_CHARS * 0.9 ? 'text-red-400' : 'text-gray-300'
              }`}
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {charCount}/{MAX_CHARS}
            </p>
          </div>

          {status === 'error' && (
            <p
              className="text-sm text-red-500"
              style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300 }}
            >
              Une erreur est survenue. Veuillez réessayer ou nous contacter par email.
            </p>
          )}

          <button
            type="submit"
            disabled={status === 'sending'}
            className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-[#1A1A1A] text-white text-sm tracking-widest uppercase whitespace-nowrap cursor-pointer hover:bg-[#333] transition-colors duration-200 rounded-lg disabled:opacity-60"
            style={{ fontFamily: 'Inter, sans-serif', fontWeight: 400 }}
          >
            {status === 'sending' ? (
              <>
                <i className="ri-loader-4-line animate-spin text-base" />
                Envoi en cours...
              </>
            ) : (
              <>
                Envoyer
                <i className="ri-send-plane-line text-base" />
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}

export default function Contact() {
  const { ref, isVisible } = useScrollReveal(0.05);

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <Navbar />
      <main className="flex-1 pt-24 pb-0">
        {/* Header */}
        <div className="px-6 md:px-10 lg:px-16 py-16 md:py-20 border-b border-gray-100">
          <div
            ref={ref}
            className={`max-w-2xl transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-px bg-gray-300" />
              <span
                className="text-xs tracking-widest uppercase text-gray-400"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Contact
              </span>
            </div>
            <h1
              className="text-gray-900"
              style={{
                fontFamily: 'Cormorant Garamond, serif',
                fontWeight: 300,
                fontSize: 'clamp(36px, 5vw, 64px)',
                lineHeight: '1.05',
              }}
            >
              Démarrons<br />
              votre <em style={{ fontStyle: 'italic' }}>projet</em>
            </h1>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 md:px-10 lg:px-16 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 max-w-7xl mx-auto">
            <ContactInfo />
            <ContactForm />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
